// @todo Currently combines export default and named exports - should probably be cleaned up

import { Clipboard } from '@capacitor/clipboard';
import { db } from 'boot/firebase';
import { i18n } from 'boot/i18n';
import { network } from 'boot/network';
import { doc, writeBatch } from 'firebase/firestore';
import { chunk, forEach, isEmpty, isFunction } from 'lodash';
import { copyToClipboard, Platform } from 'quasar';
import { notifyNegative, notifySuccess } from 'src/design-system';
import GenericIndexedDb from 'src/indexed-db/generic-indexedDb';
import { logEvent } from 'src/utils/LogEvent';

/**
 * @callback RejectCallback
 * @param {any} reason - Should be called after successful code execution
 */

export default {
  ORIGINS: {
    STAGING: 'https://hi-rasmus-staging.firebaseapp.com',
    PRODUCTION: 'https://app.hirasmus.com',
  },

  getCurrentOrigin() {
    if (process.env.MODE === 'capacitor') {
      return process.env.ENV_TYPE === 'production' ? this.ORIGINS.PRODUCTION : this.ORIGINS.STAGING;
    }

    return window.location.origin;
  },

  /**
   * @param {PromiseSettledResult<Awaited<Promise<*>>>[]} results
   * @param {RejectCallback} [rejectCallback]
   */
  handleAllSettledRejections(results, rejectCallback = null) {
    if (!results || isEmpty(results)) {
      return;
    }

    forEach(results, (result) => {
      if (result.status === 'rejected') {
        logEvent(result.reason);
        console.error(result.reason);

        if (isFunction(rejectCallback)) {
          rejectCallback(result.reason);
        }
      }
    });
  },

  /**
    * Calls Promise.allSettled({@link promises}) and automatically handle all their rejections
    * with {@link handleAllSettledRejections}
    *
    * @param {Promise[]} promises
    * @param {RejectCallback} [rejectCallback] in case of additional handling of rejections needed
    * @returns - result of Promise.allSettled({@link promises})
    */
  async allSettled(promises, rejectCallback = null) {
    const allSettledResults = await Promise.allSettled(promises);
    this.handleAllSettledRejections(allSettledResults, rejectCallback);
    return allSettledResults;
  },

  /**
   * Hash provided string with SHA-256 or SHA-1 algorithm
   *
   * @param {string} string
   * @param {'SHA-256' | 'SHA-1'} [algorithm='SHA-256']
   * @return {Promise<string>}
   */
  async hashString(string, algorithm = 'SHA-256') {
    const utf8 = new TextEncoder().encode(string);
    const hashBuffer = await crypto.subtle.digest(algorithm, utf8);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((bytes) => bytes.toString(16).padStart(2, '0')).join('');
  },

  /**
   * Check if the given argument is a plain object (not an array, not a string, not null, etc.)
   *
   * @param {any} item - the item to check
   * @return {boolean} - true if the given argument is a plain object
   */
  isPlainObject(item) {
    return typeof item === 'object'
      && !Array.isArray(item)
      && !(item instanceof String)
      && item !== null;
  },
};

export const userRoles = {
  MANAGER: 'manager',
  ADMINISTRATOR: 'administrator',
  SUPERVISOR: 'supervisor',
  SENIOR_THERAPIST: 'senior_therapist',
  TRAINER: 'trainer',
  PARENT: 'parent',
};

/**
 * Returns null if item is undefined
 *
 * @param {any} item
 * @return {null | any}
 */
export function undefinedToNull(item) {
  return item === undefined ? null : item;
}

/**
 * @param {string} email
 * @return {boolean} - true if the given argument is a valid email
 */
export function isValidEmail(email) {
  return (/^(?=[a-zA-Z0-9@._%+-]{6,254}$)[a-zA-Z0-9._%+-]{1,64}@(?:[a-zA-Z0-9-]{1,63}\.){1,8}[a-zA-Z]{2,63}$/)
    .test(email);
}

/**
 * Convert a flat object with dot-separated keys into a nested object. Helpful for
 * converting documents into Firestore-create and update-friendly format.
 * @param {object} flatObj - The flat object to convert
 * @returns {object} Nested object
 */
export function nestObject(flatObj) {
  return Object.entries(flatObj).reduce((result, [path, value]) => {
    const keys = path.split('.');

    keys.reduce((acc, key, i) => {
      acc[key] = i === keys.length - 1
        ? value
        : acc[key] ?? {};

      return acc[key];
    }, result);

    return result;
  }, {});
} // nestObject()

/**
 * Validate the mustache syntax. As per Mustache.js library will pass both `{{}}` and `{{{}}}`, cases like
 * `{{{}}`, `{{ }` or `{{ { }}` are considered errors
 *
 * @param {string} val - string to validate
 * @return {boolean} - returns the result of the validation
 */
export function mustacheValidator(val) {
  if (typeof val === 'string') {
    const openMustaches = [...val.matchAll(/{{2,3}/g)];
    const closedMustaches = [...val.matchAll(/}{2,3}/g)];
    const correctBracesNumber = openMustaches.length === closedMustaches.length;
    if (!correctBracesNumber) {
      return false;
    }

    return !closedMustaches.some((close, pairIndex) => {
      const open = openMustaches[pairIndex];
      // closing tag should be after opening
      if (close.index < open.index) {
        return true;
      }

      // only match double opening with double closing and triple opening with triple closing
      if (close[0].length !== open[0].length) {
        return true;
      }

      // closing tag should be before next opening
      const isLast = pairIndex === closedMustaches.length - 1;
      if (!isLast && (close.index > openMustaches[pairIndex + 1].index)) {
        return true;
      }

      // no dangling `{` inside mustache
      const mustacheContent = val.substring(open.index + open[0].length, close.index);
      return mustacheContent.includes('{');
    });
  }
} // mustacheValidator()

/**
 * Return the initials of the client name
 *
 * @param {string} name
 * @returns {string}
 */
export function getClientInitials(name) {
  if (!name) {
    return '';
  }

  // Remove any special characters except dots, hyphens and commas
  let cleanedName = name.toUpperCase()
    .replace(/[^A-Z0-9-,\s]/g, '')
    .replace(/\./g, '') // Remove any periods
    .replace(/^-+|-+$/g, ''); // Remove leading and trailing hyphens

  const generationSuffix = [
    'II', 'III', 'THE SECOND', 'THE THIRD',
    'JUNIOR', 'JR',
    'SENIOR', 'SR',
    'DR',
  ];

  // Remove any generation suffix from cleanedName
  const suffixRegex = new RegExp(`\\b(${generationSuffix.join('|')})\\b`, 'g');
  cleanedName = cleanedName.replace(suffixRegex, '').trim();

  // Remove everything after the first comma including the comma
  const nameWithoutComma = cleanedName.split(',')[0];

  // Split name into an array of words and remove empty strings
  const words = nameWithoutComma.split(' ').filter(Boolean);
  const firstLetter = words[0] ? words[0][0] : '';

  const [, ...rest] = words;
  const secondLetter = rest.length > 0 ? rest[rest.length - 1][0] : '';

  return `${firstLetter}${secondLetter}`;
} // getClientInitials()

/**
 * Copy a string to the clipboard — native Capacitor plugin on device, Quasar helper on web.
 *
 * Both backends can reject (insecure context, missing user activation, an unfocused document,
 * native plugin errors). The failure is caught here and surfaced as an error toast so it never
 * escapes as an unhandled rejection; the boolean return lets callers gate their success toast.
 *
 * @param {string} text
 * @return {Promise<boolean>} true when the copy succeeded, false when it failed
 */
export async function copyTextToClipboard(text) {
  try {
    if (Platform.is.capacitor) {
      await Clipboard.write({ string: text });
    } else {
      await copyToClipboard(text);
    }
    return true;
  } catch (error) {
    notifyNegative(i18n.t('common.notifyCopyToClipboardError'));
    return false;
  }
}

/**
 * Copy support info to clipboard
 *
 * @param {Record<string, string>?} additionalInfo
 * @return {Promise<void>}
 * */
export async function copySupportInfoToClipboard(additionalInfo) {
  /*
   * IMPORTED AT CALL TIME, and it has to be. At module scope this closes a cycle: `boot/indexedDb`
   * needs `allSettled` from this file, this file reached the auth store, the auth store reaches
   * `activityTypes-db`, and that extends `GenericDB` from `generic-db` — which is what pulls in
   * `boot/indexedDb` in the first place.
   *
   * The cycle is only fatal when a module graph enters it with `generic-db` first on the stack:
   * `activityTypes-db` then evaluates its `extends` while `generic-db` is still part-way through and
   * gets `undefined`. Every other entry point happens to load something in the ring earlier, which is
   * why this sat unnoticed for a year until one test came in from the other direction.
   *
   * Deferring costs nothing here. The store is only ever read inside this function, which was already
   * async, and a Pinia store cannot be used before an app exists anyway.
   */
  const { auth: useAuthStore } = await import('src/store/auth');
  const auth = useAuthStore();
  const userId = auth?.userDetails?.userId;
  const clinic = auth?.userDetails?.clinic;
  const currentClient = auth?.currentClient?.id;

  const infoPairs = [
    userId ? `user: ${userId}` : null,
    clinic ? `clinic: ${clinic}` : null,
    currentClient ? `current client: ${currentClient}` : null,
  ].filter(Boolean);

  if (additionalInfo && typeof additionalInfo === 'object') {
    Object.entries(additionalInfo).forEach(([key, value]) => {
      if (value) {
        infoPairs.push(`${key}: ${value}`);
      }
    });
  }

  const clipboardString = infoPairs.join(', ');

  if (await copyTextToClipboard(clipboardString)) {
    notifySuccess(i18n.t('common.notifyCopiedToClipboard'));
  }
}

/**
 * Show an error toast that also lets the user copy the underlying technical details
 * (e.g. `err.details?.fullMessage`) to the clipboard.
 *
 * App-level replacement for the legacy `this.notifyNegative(message, {}, { copyActionValue })`
 * mixin overload: the design-system `notifyNegative` stays presentational (it only renders and
 * themes the `actions`), while the platform clipboard access, i18n, and follow-up success toast
 * live here. When `details` is falsy the toast renders exactly like a plain error notification.
 *
 * @param {string} message - Error message shown in the toast.
 * @param {string} [details] - Technical detail copied to the clipboard when the user taps the action.
 * @return {import('quasar').NotifyDismiss | undefined}
 */
export function notifyErrorWithDetails(message, details) {
  if (!details) {
    return notifyNegative(message);
  }

  return notifyNegative(message, {
    actions: [
      // No handler → Quasar's default dismiss. Colors are applied by notifyNegative's theme.
      { label: i18n.t('common.buttonClose') },
      {
        label: i18n.t('common.errors.copyDetails'),
        handler: async () => {
          if (await copyTextToClipboard(details)) {
            notifySuccess(i18n.t('common.notifyCopiedToClipboard'));
          }
        },
      },
    ],
  });
}

/**
 * Sort array by another array
 *
 * @param {Array} arrayToSortBy
 * @param {boolean} placeMissingAtStart
 * @param {string} searchKey
 * @returns {function}
 */
export function sortByArray({ arrayToSortBy = [], placeMissingAtStart = false, searchKey = '' }) {
  return (value, nextValue) => {
    const sortValue = searchKey ? value[searchKey] : value;
    const sortNextValue = searchKey ? nextValue[searchKey] : nextValue;
    const valueIndex = arrayToSortBy.indexOf(sortValue);
    if ((valueIndex === -1) && !placeMissingAtStart) {
      return 1; // if element not found in arrayToSortBy - place it at the end
    }
    return valueIndex - arrayToSortBy.indexOf(sortNextValue);
  };
}

/**
 * When online, splits documents into Firestore batches and updates them.
 * Always updates documents in local IndexedDB.
 *
 * @param {{ id: string }[]} docsToUpdate - Array of documents, each must contain at least `id`.
 * @param {Record<string, any>} updateData - Fields to update for each document.
 * @param {string} collectionName - Firestore collection (and IndexedDB) name.
 * @returns {Promise<void>}
 */
export async function batchUpdateDocuments(docsToUpdate, updateData, collectionName) {
  try {
    const MAX_BATCH_SIZE = 500; /* https://cloud.google.com/firestore/quotas#writes_and_transactions */
    const docChunks = chunk(docsToUpdate, MAX_BATCH_SIZE);
    if (!docChunks.length) {
      return;
    }

    const firestoreBatchPromises = [];
    const indexedDbPromises = [];

    // Firestore batched writes (online)
    if (!network.offlineMode) {
      docChunks.forEach((docsChunk) => {
        const batch = writeBatch(db);

        docsChunk.forEach((docItem) => {
          if (!docItem?.id) {
            return;
          }

          const documentRef = doc(db, collectionName, docItem.id);
          batch.update(documentRef, updateData);
        });

        firestoreBatchPromises.push(batch.commit());
      });
    }

    // IndexedDB updates (offline)
    if (network.offlineMode || network.offlineModeAvailable) {
      const genericIdxDb = new GenericIndexedDb(collectionName);

      docChunks.forEach((docsChunk) => {
        indexedDbPromises.push(
          genericIdxDb.bulkUpdate(docsChunk, updateData),
        );
      });
    }

    await Promise.all([...firestoreBatchPromises, ...indexedDbPromises]);
  } catch (e) {
    console.error(e.message);
    logEvent(e);
    throw e;
  }
} // batchUpdateDocuments()

/**
 * Deep filter an object by an array of paths. Only the specified paths will be kept in the object.
 * @param {Object} obj - The object to filter
 * @param {Array<String>} filterPaths - The paths to keep in the object (e.g., ['a', 'b.c', 'd.e.f'])
 * @param {Array<String>} [path=[]] - The current path (used for recursion)
 * @return {Object} - The filtered object
 */
export function filterDeep(obj, filterPaths, path = []) {
  return Object.entries(obj).reduce(
    (acc, [key, value]) => {
      const currentPath = [...path, key];
      if (typeof value === 'object' && value !== null) {
        const childFields = filterPaths.filter(
          (fld) => fld.startsWith(`${key}.`),
        ).map((fld) => fld.slice(key.length + 1));
        acc[key] = filterDeep(value, childFields, currentPath);
      } else if (filterPaths.includes(key)) {
        acc[key] = value;
      }
      return acc;
    },
    {},
  );
}

/**
 * Check if a value is a plain object (not an array, not null, not a special object)
 * @param {any} value - The value to check
 * @return {boolean} - True if the value is a plain object, false otherwise
 */
export function isPlainObject(value) {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * Simple but efficient deep fill of two objects.
 * Primitive values and non-plain objects are overwritten.
 * Only keys present in target are filled
 * @param {Object} target - The target object to fill into
 * @param {Object} source - The source object to fill from
 * @return {Object} - The merged object (same as target)
 */
export function deepFill(target, source) {
  return Object.fromEntries(
    Object.entries(target)
      .reduce((acc, [key, tgtVal]) => {
        const srcVal = Array.isArray(source[key]) ? [...source[key]] : source[key];

        if (isPlainObject(srcVal) && ((srcVal === undefined || isPlainObject(srcVal)))) {
          // Only go deep if both source and target are plain objects or source is undefined
          acc.push([key, deepFill(
            isPlainObject(tgtVal) ? tgtVal : {},
            srcVal,
          )]);
        } else if (Array.isArray(srcVal)) {
          // Clone source array to avoid downstream mutations
          acc.push([key, [...srcVal]]);
        } else {
          acc.push([key, srcVal === undefined ? tgtVal : srcVal]);
        }
        return acc;
      }, []),
  );
} // deepFill()

/**
 * Deep merge of two objects.
 * Primitive values and non-plain objects are overwritten.
 * Both keys in the source and in the  target are filled
 * @param {Object} target - The target object to merge into
 * @param {Object} source - The source object to merge from
 * @return {Object} - The merged object
 */
export function deepMerge(target, source) {
  const result = {};

  // Merge keys from both target and source
  const keys = new Set([
    ...Object.keys(target || {}),
    ...Object.keys(source || {}),
  ]);

  Array.from(keys).forEach((key) => {
    const tgtVal = target?.[key];
    const srcVal = source?.[key];

    if (isPlainObject(tgtVal) && isPlainObject(srcVal)) {
      result[key] = deepMerge(tgtVal, srcVal);
    } else if (Array.isArray(tgtVal) && Array.isArray(srcVal)) {
      // You can choose merge strategy for arrays; here we take source
      result[key] = [...srcVal];
    } else if (srcVal !== undefined) {
      result[key] = Array.isArray(srcVal) ? [...srcVal] : srcVal;
    } else {
      // New key (doesn't exist in source) - add it from target
      result[key] = Array.isArray(tgtVal) ? [...tgtVal] : tgtVal;
    }
  });

  return result;
} // deepMerge()

/**
 * Deeply compares two objects for equality.
 * @param {*} obj1 - The first object to compare.
 * @param {*} obj2 - The second object to compare.
 * @returns {boolean} - True if the objects are equal, false otherwise.
 */
export function deepCompare(obj1, obj2) {
  if (obj1 === obj2) {
    return true;
  }

  if (typeof obj1 !== typeof obj2) {
    return false;
  }

  if (typeof obj1 !== 'object' || obj1 === null || obj2 === null) {
    return false;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }
  const allKeysMatch = keys1.every(
    (key) => keys2.includes(key) && deepCompare(obj1[key], obj2[key]),
  );
  return allKeysMatch;
} // deepCompare()

/**
 * Vanilla JS version of lodash.get()
 * @param {Object} obj
 * @param {string} path
 * @param {any} defaultValue
 * @returns {any}
 */
export function deepGet(obj, path, defaultValue = undefined) {
  if (!path.includes('.')) {
    return obj && Object.prototype.hasOwnProperty.call(obj, path) ? obj[path] : defaultValue;
  }
  const keys = path.split('.');
  const result = keys.reduce((acc, key) => {
    if (acc && Object.prototype.hasOwnProperty.call(acc, key)) {
      return acc[key];
    }
    return undefined;
  }, obj);

  return result === undefined ? defaultValue : result;
} // deepGet()

/**
 * Deeply rejects specified paths from an object. Paths should be provided in dot notation.
 * @param {Object} obj
 * @param {string[]} paths
 * @returns {Object}
 */
export function deepReject(obj, paths = []) {
  const pathSet = new Set(paths);

  function rejectHelper(currentObj, currentPath) {
    if (!isPlainObject(currentObj) && !Array.isArray(currentObj)) {
      return currentObj;
    }

    return Object.entries(currentObj).reduce((acc, [key, value]) => {
      const newPath = currentPath ? `${currentPath}.${key}` : key;

      if (pathSet.has(newPath)) {
        return acc; // Skip this key-value pair
      }

      const deepVal = rejectHelper(value, newPath);
      if (deepVal !== undefined && deepVal !== null && !Array.isArray(deepVal)
        && typeof deepVal === 'object' && Object.keys(deepVal).length === 0) {
        return acc;
      }
      acc[key] = deepVal;
      return acc;
    }, Array.isArray(currentObj) ? [] : {});
  }

  return rejectHelper(obj, ''); // Start with an empty path
} // deepReject()

export function dotNotationToDeepObject(dotObj) {
  const result = {};

  Object.entries(dotObj).forEach(([dotKey, value]) => {
    const keys = dotKey.split('.');
    let current = result;

    keys.forEach((key, index) => {
      if (index === keys.length - 1) {
        current[key] = value;
      } else {
        if (!current[key] || typeof current[key] !== 'object') {
          current[key] = {};
        }
        current = current[key];
      }
    });
  });

  return result;
} // dotNotationToDeepObject()

/**
 * Read-side counterpart to dotNotationToDeepObject: fold pending dot-notation overlay edits
 * onto a base object.
 *
 * @param {Object} source - Flat object holding both `baseKey` and `${baseKey}.<sub>` overlays
 * @param {String} baseKey - Top-level field name to resolve (e.g. 'filters' or 'targetFilters')
 * @param {Object} [options]
 * @param {*} [options.deleteMarker] - Overlay value that marks a sub-key for removal
 * @returns {Object} The merged map reflecting both saved values and pending overlay edits
 */
export function resolveDotNotationField(source, baseKey, { deleteMarker } = {}) {
  const prefix = `${baseKey}.`;
  const overlays = Object.entries(source || {}).filter(([key]) => key.startsWith(prefix));

  const setEntries = overlays.filter(([, value]) => value !== deleteMarker && value !== undefined);
  const deletePaths = overlays
    .filter(([, value]) => value === deleteMarker || value === undefined)
    .map(([key]) => key);

  const base = {
    [baseKey]: {
      ...(source?.[baseKey] || {}),
    },
  };
  let merged = deepMerge(base, dotNotationToDeepObject(Object.fromEntries(setEntries)));
  if (deletePaths.length > 0) {
    merged = deepReject(merged, deletePaths);
  }
  return merged[baseKey] || {};
} // resolveDotNotationField()

/**
 * Checks if the app is running in Capacitor environment.
 * @returns { boolean } - true if the app is running in Capacitor environment, false otherwise
 */
export function isCapacitorMode() {
  return process.env.MODE === 'capacitor';
} // isCapacitorMode()
